<template>
	<div class="opportunities-table-filters">
		<section v-if="!isProjectScopedTable" class="filters">
			<h5>{{ t("filters.dev-type-title") }}</h5>
			<div class="content filters">
				<ArkListboxDropdown
					v-model="opportunityTypes"
					:options="opportunityTypeSelectOptions"
					:multiple="true"
					:clearable="true"
					:searchable="false"
					:label="$t('filters.dev-type-title')"
					:placeholder="$t('filters.all-masculine')"
				/>
			</div>
		</section>

		<section v-if="!isProjectScopedTable" class="filters">
			<h5>{{ t("filters.opportunity") }}</h5>
			<div class="content filters">
				<ArkListboxDropdown
					v-model="projectStatus"
					:options="projectStatusSelectOptions"
					:multiple="true"
					:clearable="true"
					:searchable="false"
					:label="$t('filters.project-status')"
					:placeholder="$t('filters.all-masculine')"
				/>
				<ArkListboxDropdown
					v-model="broker"
					:options="availableBrokers"
					:multiple="true"
					:clearable="true"
					:searchable="false"
					:label="$t('filters.broker')"
					:placeholder="$t('filters.all-masculine')"
				/>
			</div>
		</section>

		<section v-if="isProjectScopedTable" class="filters">
			<h5>{{ t("filters.project") }}</h5>
			<div class="content filters">
				<ArkListboxDropdown
					v-model="projectStatus"
					:options="projectStatusSelectOptions"
					:multiple="true"
					:clearable="true"
					:searchable="false"
					:label="$t('filters.project-status')"
					:placeholder="$t('filters.all-masculine')"
				/>
				<ArkListboxDropdown
					v-if="canWriteTarget"
					v-model="priority"
					:options="opportunityPrioritySelectOptions"
					:multiple="false"
					:clearable="true"
					:searchable="false"
					:label="$t('filters.priority')"
					:placeholder="$t('filters.all-feminine')"
				/>
				<ArkListboxDropdown
					v-if="isInProjectScope"
					v-model="broker"
					:options="availableBrokers"
					:multiple="true"
					:clearable="true"
					:searchable="false"
					:label="$t('filters.broker')"
					:placeholder="$t('filters.all-masculine')"
				/>
			</div>
		</section>

		<section v-if="showClassificationFilters" class="filters">
			<h5>{{ t("filters.classification-title") }}</h5>
			<div class="content filters">
				<ArkListboxDropdown
					v-model="assetTypes"
					:options="assetTypeSelectOptions"
					:multiple="true"
					:clearable="true"
					:searchable="false"
					:label="$t('filters.asset-type')"
					:placeholder="$t('filters.all-masculine')"
				/>
			</div>

			<div class="content filters">
				<ArkListboxDropdown
					v-model="landCategories"
					:options="landCategorySelectOptions"
					:multiple="true"
					:clearable="true"
					:searchable="true"
					:label="$t('filters.land-category')"
					:placeholder="$t('filters.all-feminine')"
					:searchNormalizer="normalizeLabel"
					:disabled="shouldDisableLandCategoryFilter"
				/>
			</div>

			<div class="content filters">
				<ArkListboxDropdown
					v-model="landSubcategories"
					:options="landSubcategorySelectOptions"
					:multiple="true"
					:clearable="true"
					:searchable="true"
					:label="$t('filters.land-subcategory')"
					:placeholder="$t('filters.all-feminine')"
					:searchNormalizer="normalizeLabel"
					:disabled="shouldDisableSubcategoryFilter"
				/>
			</div>
		</section>

		<section class="filters">
			<h5>{{ t("filters.others-title") }}</h5>
			<div class="content range-filters">
				<div class="range superficy">
					<div class="values">
						<AreaRangeNotchedSlider
							v-model="superficyRange"
							:label="$t('opportunities.unitFields.superficy')"
							:min="0"
							:max="RANGE_SLIDER_MAX"
							:thresholds="[10000, 50000, 250000]"
						/>
					</div>
				</div>

				<div class="range constructible">
					<div class="values">
						<AreaRangeNotchedSlider
							v-model="constructibleRange"
							:label="$t('opportunities.unitFields.constructible')"
							:min="0"
							:max="RANGE_SLIDER_MAX"
							:thresholds="[15000, 50000, 200000]"
						/>
					</div>
				</div>

				<div class="range price">
					<div class="values">
						<RangeNotchedSlider
							v-model="priceRange"
							:label="$t('opportunities.price')"
							:min="0"
							:max="RANGE_SLIDER_MAX"
							:thresholds="[250000, 1000000, 5000000]"
							unit-prefix="$"
						/>
					</div>
				</div>

				<div class="range residential-units">
					<div class="values">
						<RangeNotchedSlider
							v-model="residentialUnitsRange"
							:label="$t('opportunities.residentialUnits')"
							:min="residentialUnitsSliderMin"
							:max="residentialUnitsSliderMax"
							:thresholds="[15, 40, 100]"
						/>
					</div>
				</div>

				<div class="range date">
					<ArkDateRangePicker
						:start-date="dateRange.min ?? undefined"
						:end-date="dateRange.max ?? undefined"
						@date-range-change="onDateRangeChange"
					/>
				</div>
			</div>
		</section>

		<section class="filters">
			<h5>{{ t("opportunities.locationAndType") }}</h5>
			<div class="content filters">
				<ArkListboxDropdown
					v-model="regions"
					:options="regionSelectOptions"
					:multiple="true"
					:clearable="true"
					:searchable="true"
					:label="$t('filters.regions-title')"
					:placeholder="$t('opportunities.all-regions')"
					:searchNormalizer="normalizeLabel"
				/>

				<ArkListboxDropdown
					v-model="municipalities"
					:options="municipalitySelectOptions"
					:multiple="true"
					:clearable="true"
					:searchable="true"
					:label="$t('filters.municipalities-title')"
					:placeholder="$t('opportunities.all-cities')"
					:searchNormalizer="normalizeLabel"
					:disabled="shouldDisableMunicipalityFilter"
				/>

				<ArkListboxDropdown
					v-model="boroughs"
					:options="boroughSelectOptions"
					:multiple="true"
					:clearable="true"
					:searchable="true"
					:label="$t('filters.boroughs-title')"
					:placeholder="$t('opportunities.all-boroughs')"
					:searchNormalizer="normalizeLabel"
					:disabled="shouldDisableBoroughFilter"
				/>
			</div>
		</section>
	</div>
</template>

<script setup lang="ts">
import { storeToRefs } from "pinia"
import { computed, ref, watch, onMounted, inject, toRaw, nextTick, type Ref } from "vue"
import { useI18n } from "vue-i18n"
import { config, Feature } from "@/AppConfig.ts"
import AreaRangeNotchedSlider from "@/components/ui/AreaRangeNotchedSlider.vue"
import ArkDateRangePicker from "@/components/ui/ArkDateRangePicker.vue"
import ArkListboxDropdown from "@/components/ui/ArkListboxDropdown.vue"
import { RANGE_SLIDER_MAX, RESIDENTIAL_UNITS_SLIDER_MAX, RESIDENTIAL_UNITS_SLIDER_MIN } from "@/components/constants"
import RangeNotchedSlider from "@/components/ui/RangeNotchedSlider.vue"
import { useView } from "@/composables/useView"
import { AssetType, LandCategory, LandSubcategory, OpportunityType } from "@/graphql"
import { type BoroughRecord, EvaluationStatus, type Municipality, OpportunityPriority } from "@/model/DataModel.ts"
import { Permission, UseRootStore } from "@/model/RootStore"
import { isHuntingMode, ProjectStoreKey } from "@/stores/projectStore.ts"
import { ViewScope } from "@/types"
import { isRealBroker } from "@/utils/brokers"
import { evaluationStatusTranslationKeys } from "@/utils/filterTranslationKeys"

const { t } = useI18n()
const store = UseRootStore()
const props = defineProps<{
	brokerNameSources: BrokerNameSource[]
}>()
const { isUserLoggedIn, currentUser } = storeToRefs(store)
const view = useView(ViewScope.OpportunitiesTable)
const huntingView = useView(ViewScope.OpportunitiesHuntingTable)

// Project scope functionality (read-only context, still real - only the filter VALUES below are local placeholders)
const projectStore = inject(ProjectStoreKey)!()
const isInProjectScope = ref(false)
const canWriteTarget = computed(() => isUserLoggedIn.value && currentUser.value.has(Permission.OpportunityTargetWrite))

const isProjectScopedTable = computed(() => isHuntingMode())
const activeView = computed(() => (isProjectScopedTable.value ? huntingView : view))

// placeholders only: not wired to storeFilters or any live filtering yet
const opportunityTypes = ref<string[]>([])
const projectStatus = ref<string[]>([])
const priority = ref<string | null>(null)
const broker = ref<string[]>([])
const assetTypes = ref<string[]>([])
const landCategories = ref<string[]>([])
const landSubcategories = ref<string[]>([])
const superficyRange = ref<[number, number]>([0, RANGE_SLIDER_MAX])
const constructibleRange = ref<[number, number]>([0, RANGE_SLIDER_MAX])
const priceRange = ref<[number, number]>([0, RANGE_SLIDER_MAX])
const residentialUnitsSliderMin = RESIDENTIAL_UNITS_SLIDER_MIN
const residentialUnitsSliderMax = RESIDENTIAL_UNITS_SLIDER_MAX
const residentialUnitsRange = ref<[number, number]>([residentialUnitsSliderMin, residentialUnitsSliderMax])
const dateRange = ref<{ min: Date | null; max: Date | null }>({ min: null, max: null })
const regions = ref<string[]>([])
const municipalities = ref<string[]>([])
const boroughs = ref<string[]>([])
let isResettingFilters = false
let isApplyingViewFilters = false
let isFilterPanelHydrated = false

function onDateRangeChange(data: { start: Date | null; end: Date | null }) {
	dateRange.value = { min: data.start, max: data.end }
}

function syncFilterValue<T>(source: () => T, update: (value: T) => void): void {
	watch(
		source,
		value => {
			if (!isFilterPanelHydrated || isResettingFilters || isApplyingViewFilters) {
				return
			}

			update(value)
		},
		{ deep: true }
	)
}

syncFilterValue(
	() => opportunityTypes.value,
	value => activeView.value.setOpportunityTypes(value)
)
syncFilterValue(
	() => projectStatus.value,
	value => activeView.value.setEvaluationStatus(value)
)
syncFilterValue(
	() => priority.value,
	value => activeView.value.setPriority(value)
)
syncFilterValue(
	() => broker.value,
	value => activeView.value.setBrokers(value)
)
syncFilterValue(
	() => assetTypes.value,
	value => activeView.value.setAssetTypes(value)
)
syncFilterValue(
	() => landCategories.value,
	value => activeView.value.setLandCategories(value)
)
syncFilterValue(
	() => landSubcategories.value,
	value => activeView.value.setLandSubcategories(value)
)
syncFilterValue(
	() => superficyRange.value,
	value => activeView.value.setLandAreaRange(value)
)
syncFilterValue(
	() => constructibleRange.value,
	value => activeView.value.setBuildableLandAreaRange(value)
)
syncFilterValue(
	() => priceRange.value,
	value => activeView.value.setPriceRange(value)
)
syncFilterValue(
	() => residentialUnitsRange.value,
	value => activeView.value.setResidentialUnitsRange(value)
)
syncFilterValue(
	() => dateRange.value,
	value => activeView.value.setLaunchDateRange(value)
)
syncFilterValue(
	() => regions.value,
	value => activeView.value.setRegions(value)
)
syncFilterValue(
	() => municipalities.value,
	value => activeView.value.setMunicipalities(value)
)
syncFilterValue(
	() => boroughs.value,
	value => activeView.value.setBoroughs(value)
)

function dateValue(value: string | null | undefined): Date | null {
	if (!value) {
		return null
	}

	const date = new Date(`${value}T00:00:00.000`)
	return Number.isNaN(date.getTime()) ? null : date
}

function rangeValue(
	range: { min?: number | null; max?: number | null } | null | undefined,
	defaultRange: [number, number]
): [number, number] {
	return [range?.min ?? defaultRange[0], range?.max ?? defaultRange[1]]
}

function areArraysEqual(left: readonly string[], right: readonly string[]): boolean {
	return left.length === right.length && left.every((value, index) => value === right[index])
}

function setArrayIfChanged(target: Ref<string[]>, value: string[]): void {
	if (!areArraysEqual(target.value, value)) {
		target.value = value
	}
}

function setValueIfChanged<T>(target: Ref<T>, value: T): void {
	if (target.value !== value) {
		target.value = value
	}
}

function setRangeIfChanged(target: Ref<[number, number]>, value: [number, number]): void {
	if (target.value[0] !== value[0] || target.value[1] !== value[1]) {
		target.value = value
	}
}

function setDateRangeIfChanged(
	target: Ref<{ min: Date | null; max: Date | null }>,
	value: { min: Date | null; max: Date | null }
): void {
	const currentMin = target.value.min?.getTime() ?? null
	const currentMax = target.value.max?.getTime() ?? null
	const nextMin = value.min?.getTime() ?? null
	const nextMax = value.max?.getTime() ?? null

	if (currentMin !== nextMin || currentMax !== nextMax) {
		target.value = value
	}
}

watch(
	() => [activeView.value.view.value.scope, activeView.value.view.value.filter?.opportunity] as const,
	([, filter]) => {
		isApplyingViewFilters = true

		setArrayIfChanged(opportunityTypes, filter?.opportunityTypes ?? [])
		setArrayIfChanged(projectStatus, filter?.evaluationStatus ?? [])
		setValueIfChanged(priority, filter?.priority ?? null)
		setArrayIfChanged(broker, filter?.brokers ?? [])
		setArrayIfChanged(assetTypes, filter?.assetTypes ?? [])
		setArrayIfChanged(landCategories, filter?.landCategories ?? [])
		setArrayIfChanged(landSubcategories, filter?.landSubcategories ?? [])
		setRangeIfChanged(superficyRange, rangeValue(filter?.landArea, [0, RANGE_SLIDER_MAX]))
		setRangeIfChanged(constructibleRange, rangeValue(filter?.buildableLandArea, [0, RANGE_SLIDER_MAX]))
		setRangeIfChanged(priceRange, rangeValue(filter?.price, [0, RANGE_SLIDER_MAX]))
		setRangeIfChanged(
			residentialUnitsRange,
			rangeValue(filter?.residentialUnits, [residentialUnitsSliderMin, residentialUnitsSliderMax])
		)
		setDateRangeIfChanged(dateRange, {
			min: dateValue(filter?.launchDate?.min),
			max: dateValue(filter?.launchDate?.max),
		})
		setArrayIfChanged(regions, filter?.regions ?? [])
		setArrayIfChanged(municipalities, filter?.municipalities ?? [])
		setArrayIfChanged(boroughs, filter?.boroughs ?? [])

		nextTick(() => {
			isApplyingViewFilters = false
		})
	},
	{ deep: true, immediate: true }
)

// project status -> single
const projectStatusSelectOptions = computed(() =>
	projectStatusOptions.value.map(o => ({ value: o.id, label: o.label }))
)

const opportunityTypeSelectOptions = computed(() =>
	Object.values(OpportunityType)
		.filter(value => value !== OpportunityType.OpportunityTarget)
		.map(value => ({
			value,
			label: t(`opportunities-layers.${value}`),
		}))
)

const opportunityPrioritySelectOptions = computed(() => [
	{ value: "all", label: t("filters.priority-all") },
	...Object.values(OpportunityPriority).map(value => ({
		value,
		label: t(`opportunities-table.priority.${value}`),
	})),
])

// regions / municipalities / boroughs -> multi (options come from real reference data, selection is local)
const regionSelectOptions = computed(() => regionItems.value.map(o => ({ value: o.id, label: o.label })))

const municipalitySelectOptions = computed(() => municipalityItems.value.map(o => ({ value: o.id, label: o.label })))

const boroughSelectOptions = computed(() => boroughItems.value.map(o => ({ value: o.id, label: o.label })))

const selectedRegions = computed(() => new Set<string>(regions.value))

const selectedMunicipalities = computed(() => new Set<string>(municipalities.value))

const shouldDisableMunicipalityFilter = computed(() => selectedRegions.value.size === 0)

const shouldDisableBoroughFilter = computed(() => selectedMunicipalities.value.size === 0)

const availableRegionNames = ref<string[]>([])
const availableMunicipalityRecords = ref<Municipality[]>([])
const availableBoroughRecords = ref<BoroughRecord[]>([])

// classification filters
const showClassificationFilters = computed(() => config.ui.has(Feature.DevTypes))
const shouldDisableLandCategoryFilter = computed(() => {
	if (!assetTypes.value.length) return false
	return !assetTypes.value.includes(AssetType.Land)
})

const shouldShowLandCategoryFilter = computed(() => {
	if (!assetTypes.value.length) return true // Show by default
	return assetTypes.value.includes(AssetType.Land)
})

const shouldShowSubcategoryFilter = computed(() => {
	if (!shouldShowLandCategoryFilter.value) return false
	if (!landCategories.value.length) return false

	const relevantCategories = [
		LandCategory.Residential,
		LandCategory.Commercial,
		LandCategory.Industrial,
		LandCategory.Agricultural,
	]
	return landCategories.value.some((cat: any) => relevantCategories.includes(cat))
})

const shouldDisableSubcategoryFilter = computed(() => {
	if (shouldDisableLandCategoryFilter.value) return true
	if (!landCategories.value.length) return true

	const relevantCategories = [
		LandCategory.Residential,
		LandCategory.Commercial,
		LandCategory.Industrial,
		LandCategory.Agricultural,
	]
	return !landCategories.value.some((cat: any) => relevantCategories.includes(cat))
})

const assetTypeSelectOptions = computed(() => [
	...Object.values(AssetType).map(value => ({
		value,
		label: t(`property.assetType.${value}`),
	})),
	{ value: "none", label: t("property.assetType.none") },
])

const landCategorySelectOptions = computed(() =>
	Object.values(LandCategory).map(value => ({
		value,
		label: t(`property.landCategory.${value}`),
	}))
)

const landSubcategorySelectOptions = computed(() => {
	if (!landCategories.value.length) return []

	const options = []

	// Residential
	if (landCategories.value.includes(LandCategory.Residential)) {
		options.push(
			{ value: LandSubcategory.LowRise, label: t("property.landSubcategory.LowRise") },
			{ value: LandSubcategory.MidRise, label: t("property.landSubcategory.MidRise") },
			{ value: LandSubcategory.HighRise, label: t("property.landSubcategory.HighRise") },
			{ value: LandSubcategory.TownHouse, label: t("property.landSubcategory.TownHouse") },
			{ value: LandSubcategory.SingleFamily, label: t("property.landSubcategory.SingleFamily") }
		)
	}

	// Commercial
	if (landCategories.value.includes(LandCategory.Commercial)) {
		options.push(
			{ value: LandSubcategory.LowRise, label: t("property.landSubcategory.LowRise") },
			{ value: LandSubcategory.MidRise, label: t("property.landSubcategory.MidRise") },
			{ value: LandSubcategory.HighRise, label: t("property.landSubcategory.HighRise") }
		)
	}

	// Industrial
	if (landCategories.value.includes(LandCategory.Industrial)) {
		options.push(
			{ value: LandSubcategory.LightMedium, label: t("property.landSubcategory.LightMedium") },
			{ value: LandSubcategory.Heavy, label: t("property.landSubcategory.Heavy") }
		)
	}

	// Agricultural
	if (landCategories.value.includes(LandCategory.Agricultural)) {
		options.push(
			{ value: LandSubcategory.FullyCultivated, label: t("property.landSubcategory.FullyCultivated") },
			{ value: LandSubcategory.MostlyCultivated, label: t("property.landSubcategory.MostlyCultivated") },
			{ value: LandSubcategory.HalfAndHalf, label: t("property.landSubcategory.HalfAndHalf") },
			{ value: LandSubcategory.MostlyForestry, label: t("property.landSubcategory.MostlyForestry") },
			{ value: LandSubcategory.FullForestry, label: t("property.landSubcategory.FullForestry") }
		)
	}

	const seen = new Set()
	return options.filter(option => {
		if (seen.has(option.value)) return false
		seen.add(option.value)
		return true
	})
})

// Project status filter options
const projectStatusOptions = computed(() => [
	{ id: EvaluationStatus.Unset, label: t(evaluationStatusTranslationKeys[EvaluationStatus.Unset]) },
	{ id: EvaluationStatus.Like, label: t(evaluationStatusTranslationKeys[EvaluationStatus.Like]) },
	{ id: EvaluationStatus.Dislike, label: t(evaluationStatusTranslationKeys[EvaluationStatus.Dislike]) },
])

type BrokerNameSource = {
	broker_first_name?: string | null
	broker_last_name?: string | null
	collaborative_broker_names?: string[] | null
}

const availableBrokers = computed(() => {
	const names = new Set<string>()
	for (const opportunity of props.brokerNameSources) {
		const primaryName = [opportunity.broker_first_name, opportunity.broker_last_name].filter(Boolean).join(" ")
		if (primaryName) names.add(primaryName)
		for (const collabName of opportunity.collaborative_broker_names ?? []) {
			if (collabName) names.add(collabName)
		}
	}
	// restrict to the broker name file
	return [...names]
		.filter(isRealBroker)
		.sort()
		.map(name => ({ value: name, label: name }))
})

onMounted(async () => {
	try {
		// avoid re-fetching if projectStore (a shared singleton) is already populated
		if (projectStore.ready) {
			isInProjectScope.value = !!projectStore.currentProject
		} else {
			projectStore.fetchCurrentProjectMetadata().then(project => {
				isInProjectScope.value = !!(project && project.value)
			})
		}
	} finally {
		await nextTick()
		isFilterPanelHydrated = true
	}
})

async function loadGeographicOptions(): Promise<void> {
	const [regionNames, municipalityRecords, loadedBoroughs] = await Promise.all([
		store.getRegions,
		store.getMunicipalityRecords,
		store.getBoroughRecords,
	])
	availableRegionNames.value = regionNames
	availableMunicipalityRecords.value = municipalityRecords
	availableBoroughRecords.value = loadedBoroughs
}

watch(
	() => projectStore.currentProject,
	project => {
		isInProjectScope.value = !!project
	}
)

watch(landCategories, () => {
	if (!shouldShowSubcategoryFilter.value) {
		landSubcategories.value = []
		return
	}

	// Keep only valid subcategories
	if (landSubcategories.value.length) {
		const validValues: string[] = landSubcategorySelectOptions.value.map(o => o.value)
		landSubcategories.value = landSubcategories.value.filter(sub => validValues.includes(sub))
	}
})

// Normalize a label by removing accents and lowercasing it
// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/normalize
function normalizeLabel(name: string) {
	return name.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase()
}

const regionItems = computed(() =>
	toRaw(availableRegionNames.value).map(name => ({
		id: name,
		label: name,
	}))
)

const hasMunicipalityRegionData = computed(() =>
	availableMunicipalityRecords.value.some(municipality => municipality.regionName)
)

const municipalityItems = computed(() => {
	const municipalityRecords = toRaw(availableMunicipalityRecords.value)
	const filteredMunicipalities = hasMunicipalityRegionData.value
		? municipalityRecords.filter(
				municipality => municipality.regionName && selectedRegions.value.has(municipality.regionName)
			)
		: municipalityRecords

	return filteredMunicipalities
		.map(municipality => ({
			id: municipality.name,
			label: municipality.name,
		}))
		.sort((left, right) => left.label.localeCompare(right.label))
})

const boroughItems = computed(() =>
	toRaw(availableBoroughRecords.value)
		.filter(borough => borough.municipalityName && selectedMunicipalities.value.has(borough.municipalityName))
		.map(borough => ({
			id: borough.name,
			label: borough.name,
		}))
		.sort((left, right) => left.label.localeCompare(right.label))
)

function filterValuesByOptions(values: string[], options: { value: string }[]): string[] {
	const validValues = new Set(options.map(option => option.value))
	return values.filter(value => validValues.has(value))
}

watch([regions, () => municipalitySelectOptions.value.map(option => option.value)], () => {
	setArrayIfChanged(
		municipalities,
		shouldDisableMunicipalityFilter.value
			? []
			: filterValuesByOptions(municipalities.value, municipalitySelectOptions.value)
	)
})

watch([municipalities, () => boroughSelectOptions.value.map(option => option.value)], () => {
	setArrayIfChanged(
		boroughs,
		shouldDisableBoroughFilter.value ? [] : filterValuesByOptions(boroughs.value, boroughSelectOptions.value)
	)
})

const resetFilters = () => {
	isResettingFilters = true
	opportunityTypes.value = []
	projectStatus.value = []
	priority.value = null
	broker.value = []
	assetTypes.value = []
	landCategories.value = []
	landSubcategories.value = []
	superficyRange.value = [0, RANGE_SLIDER_MAX]
	constructibleRange.value = [0, RANGE_SLIDER_MAX]
	priceRange.value = [0, RANGE_SLIDER_MAX]
	residentialUnitsRange.value = [residentialUnitsSliderMin, residentialUnitsSliderMax]
	dateRange.value = { min: null, max: null }
	regions.value = []
	municipalities.value = []
	boroughs.value = []
	activeView.value.resetFilter()
	nextTick(() => {
		isResettingFilters = false
	})
}

const activeFiltersCount = computed(() => {
	const isRangeActive = (range: [number, number], [min, max]: [number, number]) => range[0] !== min || range[1] !== max

	return [
		opportunityTypes.value.length > 0,
		projectStatus.value.length > 0,
		priority.value != null,
		broker.value.length > 0,
		assetTypes.value.length > 0,
		landCategories.value.length > 0,
		landSubcategories.value.length > 0,
		isRangeActive(superficyRange.value, [0, RANGE_SLIDER_MAX]),
		isRangeActive(constructibleRange.value, [0, RANGE_SLIDER_MAX]),
		isRangeActive(priceRange.value, [0, RANGE_SLIDER_MAX]),
		isRangeActive(residentialUnitsRange.value, [residentialUnitsSliderMin, residentialUnitsSliderMax]),
		dateRange.value.min != null || dateRange.value.max != null,
		regions.value.length > 0,
		municipalities.value.length > 0,
		boroughs.value.length > 0,
	].filter(Boolean).length
})

function hasActiveFilters(): boolean {
	return activeFiltersCount.value > 0
}

defineExpose({ resetFilters, hasActiveFilters, activeFiltersCount, loadGeographicOptions })
</script>

<style scoped>
.opportunities-table-filters {
	flex-grow: 1;
	display: flex;
	flex-direction: column;
	gap: var(--spacing-lg);

	section {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-sm);

		&:not(:last-child) {
			border-bottom: var(--border-width-thin) solid var(--neutral-200);
			padding-bottom: var(--spacing-lg);
		}
	}

	h5 {
		margin: 0 0 var(--spacing-xs);
		color: var(--neutral-900);
		font-family: var(--font-family-primary);
		font-size: var(--font-size-xs);
		font-weight: var(--font-weight-semibold);
		text-transform: uppercase;
		letter-spacing: 0.02em;
	}

	.content {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-md);
	}

	.range-filters {
		padding: 0;
		padding-bottom: var(--spacing-lg);

		.range {
			--input-width: var(--width-input-field);

			.values {
				margin-top: var(--spacing-sm);
			}
		}
	}

	.filters {
		min-height: var(--spacing-2xl);
		font-size: var(--font-size-small);
		width: 100%;
		position: relative;
	}
}
</style>
